import { confirmar } from '@/components/ui/confirm-dialog';
import { useEffect, useState, useMemo } from 'react';
import { apiClient } from '@/lib/api';
import { ENDPOINTS } from '@/lib/api/endpoints';
import { useToast } from '@/lib/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Pencil, Loader2 } from 'lucide-react';
import type { Currency } from '../types/master-data.types';

interface ExchangeRateEntry {
  id: string;
  tenantId: string;
  fromCurrencyCode: string;
  toCurrencyCode: string;
  rate: number;
  effectiveDate: string;
  source: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ExchangeRateManagerProps {
  currencies: Currency[];
}

const DEFAULT_SOURCE = 'manual';

function todayIso(): string {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export function ExchangeRateManager({ currencies }: ExchangeRateManagerProps) {
  const { toast } = useToast();
  const [rates, setRates] = useState<ExchangeRateEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ExchangeRateEntry | null>(null);

  const [form, setForm] = useState({
    fromCurrencyCode: '',
    toCurrencyCode: '',
    rate: '',
    effectiveDate: todayIso(),
    source: DEFAULT_SOURCE,
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const currencyOptions = useMemo(
    () =>
      currencies
        .filter((c) => c.isActive)
        .map((c) => ({ code: c.code.toUpperCase(), label: `${c.code} — ${c.name}` })),
    [currencies]
  );

  const loadRates = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<ExchangeRateEntry[]>(ENDPOINTS.EXCHANGE_RATES);
      setRates(response);
    } catch (error: any) {
      toast({
        title: 'Error al cargar tipos de cambio',
        description: error?.message || 'No se pudo obtener el listado',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      fromCurrencyCode: '',
      toCurrencyCode: '',
      rate: '',
      effectiveDate: todayIso(),
      source: DEFAULT_SOURCE,
      notes: '',
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (entry: ExchangeRateEntry) => {
    setEditing(entry);
    setForm({
      fromCurrencyCode: entry.fromCurrencyCode,
      toCurrencyCode: entry.toCurrencyCode,
      rate: String(entry.rate),
      effectiveDate: entry.effectiveDate.slice(0, 10),
      source: entry.source,
      notes: entry.notes ?? '',
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.fromCurrencyCode) errs.fromCurrencyCode = 'Seleccioná la moneda origen';
    if (!form.toCurrencyCode) errs.toCurrencyCode = 'Seleccioná la moneda destino';
    if (
      form.fromCurrencyCode &&
      form.toCurrencyCode &&
      form.fromCurrencyCode === form.toCurrencyCode
    ) {
      errs.toCurrencyCode = 'Debe ser distinta a la moneda origen';
    }
    const rateNum = Number(form.rate);
    if (!form.rate || Number.isNaN(rateNum) || rateNum <= 0) {
      errs.rate = 'Ingresá un número positivo';
    }
    if (!form.effectiveDate) errs.effectiveDate = 'Seleccioná una fecha';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const payload = {
        fromCurrencyCode: form.fromCurrencyCode.toUpperCase(),
        toCurrencyCode: form.toCurrencyCode.toUpperCase(),
        rate: Number(form.rate),
        effectiveDate: form.effectiveDate,
        source: form.source || DEFAULT_SOURCE,
        notes: form.notes || null,
      };

      if (editing) {
        await apiClient.patch(ENDPOINTS.EXCHANGE_RATE_BY_ID(editing.id), payload);
        toast({ title: 'Tipo de cambio actualizado' });
      } else {
        await apiClient.post(ENDPOINTS.EXCHANGE_RATES, payload);
        toast({ title: 'Tipo de cambio registrado' });
      }
      setDialogOpen(false);
      await loadRates();
    } catch (error: any) {
      toast({
        title: 'No se pudo guardar',
        description: error?.message || 'Verificá los datos e intentá de nuevo',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (entry: ExchangeRateEntry) => {
    if (
      !(await confirmar({
        title: `¿Eliminar el tipo de cambio ${entry.fromCurrencyCode}→${entry.toCurrencyCode} del ${entry.effectiveDate.slice(0, 10)}?`,
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    ) {
      return;
    }
    try {
      await apiClient.delete(ENDPOINTS.EXCHANGE_RATE_BY_ID(entry.id));
      toast({ title: 'Tipo de cambio eliminado' });
      await loadRates();
    } catch (error: any) {
      toast({
        title: 'No se pudo eliminar',
        description: error?.message || 'Intentá de nuevo',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Tipos de cambio por fecha, por par de monedas. Se usan al jalar costos desde una
          cotización upstream con moneda distinta a la downstream.
        </p>
        <Button onClick={openCreate} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo TC
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cargando...
        </div>
      ) : rates.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No hay tipos de cambio registrados todavía. Agregá el primero para habilitar la conversión
          automática de costos entre monedas.
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha efectiva</TableHead>
                <TableHead>Par</TableHead>
                <TableHead className="text-right">Tasa</TableHead>
                <TableHead>Fuente</TableHead>
                <TableHead>Notas</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rates.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">
                    {r.effectiveDate.slice(0, 10)}
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{r.fromCurrencyCode}</span>
                    <span className="mx-1 text-muted-foreground">→</span>
                    <span className="font-medium">{r.toCurrencyCode}</span>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {r.rate.toLocaleString('es-BO', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 6,
                    })}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{r.source}</Badge>
                  </TableCell>
                  <TableCell className="max-w-[240px] truncate text-xs text-muted-foreground">
                    {r.notes || '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(r)}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar tipo de cambio' : 'Nuevo tipo de cambio'}</DialogTitle>
            <DialogDescription>
              Un monto en moneda origen se multiplica por la tasa para obtener el equivalente en
              moneda destino.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Moneda origen *</Label>
              <Select
                value={form.fromCurrencyCode}
                onValueChange={(v) => setForm({ ...form, fromCurrencyCode: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {currencyOptions.map((c) => (
                    <SelectItem key={`from-${c.code}`} value={c.code}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formErrors.fromCurrencyCode && (
                <p className="text-xs text-destructive">{formErrors.fromCurrencyCode}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Moneda destino *</Label>
              <Select
                value={form.toCurrencyCode}
                onValueChange={(v) => setForm({ ...form, toCurrencyCode: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {currencyOptions.map((c) => (
                    <SelectItem key={`to-${c.code}`} value={c.code}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formErrors.toCurrencyCode && (
                <p className="text-xs text-destructive">{formErrors.toCurrencyCode}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tasa *</Label>
              <Input
                type="number"
                step="0.000001"
                min="0"
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: e.target.value })}
                placeholder="Ej: 6.96"
              />
              {formErrors.rate && <p className="text-xs text-destructive">{formErrors.rate}</p>}
            </div>

            <div className="space-y-2">
              <Label>Fecha efectiva *</Label>
              <Input
                type="date"
                value={form.effectiveDate}
                onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
              />
              {formErrors.effectiveDate && (
                <p className="text-xs text-destructive">{formErrors.effectiveDate}</p>
              )}
            </div>

            <div className="space-y-2 col-span-2">
              <Label>Fuente</Label>
              <Input
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                placeholder="manual, bcb, criptoya_p2p, etc."
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label>Notas</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Opcional"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? 'Guardar cambios' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ExchangeRateManager;
